import React from 'react'
import { createRoot } from 'react-dom/client'
import {
  SocialMediaBar,
  FooterNavigation,
  FooterDisclaimer
} from '@ampcore/design-system'
import Cookies from 'js-cookie'
import Analytics from '../../components/SiteNav/analytics.js'

export default class AmeripriseFooterElement extends HTMLElement {
  mountPoint!: HTMLDivElement
  menuName: string | null = null
  version: string | null = null
  customerServiceHeading: string | null = null
  customerServiceDescription: string | null = null
  customerServiceLink: string | null = null
  alwaysShowOptOutLink = false
  shadowRootAssets: HTMLTemplateElement | null = null

  connectedCallback() {
    this.mountPoint = document.createElement('div')
    this.menuName = this.getAttribute('data-menu-name')
    this.version = this.getAttribute('data-version')
    this.customerServiceHeading = this.getAttribute('data-customer-heading')
    this.customerServiceDescription = this.getAttribute(
      'data-customer-description'
    )
    this.customerServiceLink = this.getAttribute('data-customer-link')
    const findAnAdvisorBtnText = this.getAttribute(
      'data-find-an-advisor-btn-text'
    )
    const findAnAdvisorLink = this.getAttribute('data-find-an-advisor-btn-url')
    this.alwaysShowOptOutLink =
      this.getAttribute('data-always-show-opt-out-link') === 'true'

    // TODO: determine whether logic can be removed
    // force other domains to always display Find an Advisor to logged in and expired clients
    const host = window.location.hostname
    const isAmeripriseDotCom =
      /localhost/.test(host) || /\.ameriprise\.com/.test(host)
    const userStatus = Cookies.get('lastAmpAuthState') ?? 'unrecognized'
    let isFindAnAdvisorShown
    if (isAmeripriseDotCom) {
      isFindAnAdvisorShown = userStatus !== 'active' && userStatus !== 'expired'
    } else {
      isFindAnAdvisorShown = true
    }

    const rightsReserved = this.getAttribute('data-rights-reserved')

    // Check for data-use-shadow-root attribute (default false)
    const useShadowRootAttr = this.getAttribute('data-use-shadow-root')
    const useShadowRoot = useShadowRootAttr === 'true'

    let mountContainer: HTMLDivElement
    if (useShadowRoot) {
      const shadowRoot = this.attachShadow({ mode: 'open' })
      shadowRoot.appendChild(this.mountPoint)
      this.shadowRootAssets = this.querySelector(
        '#shadow-root-assets'
      ) as HTMLTemplateElement | null
      if (this.shadowRootAssets?.content) {
        shadowRoot.appendChild(this.shadowRootAssets.content)
      }
      mountContainer = this.mountPoint
    } else {
      this.appendChild(this.mountPoint)
      mountContainer = this.mountPoint
    }

    const root = createRoot(mountContainer)

    function handleClick(event: MouseEvent) {
      const disclaimerMessage = (event.currentTarget as HTMLElement)?.innerText
      try {
        if (disclaimerMessage === 'Find an Advisor') {
          const customAnalytics = new Analytics()
          customAnalytics.setSatelliteTrack('initiativeFindAnAdvisor')
        }
        const disclaimerAnalytics = new Analytics()
        disclaimerAnalytics.ping(disclaimerMessage, 'footer')
      } catch (e) {
        console.log(e)
      }
    }

    let links: unknown[] = []
    const linksAttr = this.getAttribute('data-links')
    if (linksAttr) {
      try {
        const parsed = JSON.parse(linksAttr)
        if (Array.isArray(parsed)) {
          links = parsed
        }
      } catch (e) {
        console.warn('Invalid data-links JSON:', e)
      }
    }

    root.render(
      <div className="u-adel-reset">
        <div className="u-block" style={{ display: 'none' }}>
          <SocialMediaBar
            content={{
              isFindAnAdvisorShown,
              findAnAdvisorBtnText: findAnAdvisorBtnText ?? '',
              findAnAdvisorLink: findAnAdvisorLink ?? '',
              handleClick: handleClick as unknown as React.MouseEventHandler
            }}
          />
          <FooterNavigation
            menuName={this.menuName ?? undefined}
            version={this.version ?? undefined}
            customerServiceHeading={this.customerServiceHeading ?? ''}
            customerServiceDescription={this.customerServiceDescription ?? ''}
            customerServiceLink={this.customerServiceLink ?? ''}
            alwaysShowOptOutLink={this.alwaysShowOptOutLink}
          ></FooterNavigation>
          <FooterDisclaimer
            links={links as never}
            rightsReserved={rightsReserved ?? ''}
            userAgreeTerms=""
          />
        </div>
      </div>
    )
  }
}

if (!window.customElements.get('ameriprise-footer')) {
  window.customElements.define('ameriprise-footer', AmeripriseFooterElement)
}
